/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import ModuleLayout, { topImageOnPhone } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import PreservedUtmButton from 'bundles/cds-labs/components/Modules/components/PreservedUtmButton';
import type { VideoItem } from 'bundles/cds-labs/components/Modules/components/Video';
import Video from 'bundles/cds-labs/components/Modules/components/Video';
import { isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';

export type InformationModuleCtaVariants = Extract<ButtonProps['variant'], 'secondary' | 'ghost'>;

type CallToAction = {
  label: string | null;
  target: string | null;
  variant?: InformationModuleCtaVariants;
};

type Props = {
  cta?: CallToAction | null;
  description?: React.ReactNode;
  headline?: string | null;
  image?: ImageType | null;
  secondaryCta?: CallToAction | null;
  video?: VideoItem | null;
  customPrimaryCta?: React.ReactNode;
} & ModuleLayoutConfig;

const styles = {
  // TODO: the `xs` style is to show a small spacing between the image(top) and the content(bottom) on phone.
  // See the same TODO in `Hero` for details.
  content: css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-100);
    }
  `,

  headline: css`
    margin-bottom: var(--cds-spacing-200);
  `,

  ctaSection: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,

  ghostAlignment: css`
    margin-left: -16px;
  `,

  imageWrapper: css`
    margin: 0 auto;
    border-radius: var(--cds-border-radius-100);
    overflow: hidden;
  `,
  secondaryCta: css`
    padding: 0;
  `,
  customPrimaryCtaSectionGap: css`
    gap: var(--cds-spacing-400);
  `,
};

function InformationModule(props: Props) {
  const track = useTracker();
  const { image, video, description, customPrimaryCta } = props;
  const isInverted = isInvertedColor(props.backgroundColor ?? '');

  const imagePanel = () => (
    <div css={styles.imageWrapper}>
      {image && video && (
        <Video image={image} video={video} preloadImage={props.preloadImage} lazyLoadImage={props.lazyLoadImage} />
      )}
      {image && !video && <Image {...image} shouldPreload={props.preloadImage} shouldLazyLoad={props.lazyLoadImage} />}
    </div>
  );

  let ctaVariant: ButtonProps['variant'] = 'secondary';
  if (props?.cta?.variant === 'ghost') {
    ctaVariant = isInverted ? 'ghostInvert' : 'ghost';
  }

  let secondaryCtaVariant: ButtonProps['variant'] = isInverted ? 'ghostInvert' : 'ghost';
  if (props.secondaryCta?.variant === 'secondary') {
    secondaryCtaVariant = 'secondary';
  }

  const trackClick = (trackingName: ButtonName, linkTarget: string) => () => {
    track('click_button', {
      button: { name: trackingName, linkURL: linkTarget },
      pageSection: {
        sectionName: 'information',
      },
    });
  };

  const contentPanel = () => (
    <div css={styles.content}>
      {props?.headline && (
        <Typography2 variant="titleSmall" component="h2" css={styles.headline} color="inherit">
          {props.headline}
        </Typography2>
      )}
      {description}
      {((props?.cta?.label && props?.cta?.target) || customPrimaryCta) && (
        <div
          css={[
            styles.ctaSection,
            props?.cta?.variant === 'ghost' ? styles.ghostAlignment : null,
            customPrimaryCta && props?.secondaryCta?.variant !== 'secondary' ? styles.customPrimaryCtaSectionGap : null,
          ]}
        >
          {customPrimaryCta ?? (
            <PreservedUtmButton
              variant={ctaVariant}
              href={props.cta?.target}
              trackingName="information_primary_cta"
              icon={props?.cta?.variant === 'ghost' ? <ArrowNextIcon /> : undefined}
              onClick={trackClick('promo_template_primary_cta', props.cta?.target as string)}
            >
              {props.cta?.label}
            </PreservedUtmButton>
          )}

          {props?.secondaryCta?.label && props?.secondaryCta?.target && (
            <PreservedUtmButton
              css={customPrimaryCta && props?.secondaryCta?.variant !== 'secondary' ? styles.secondaryCta : null}
              variant={secondaryCtaVariant}
              href={props.secondaryCta?.target}
              trackingName="information_secondary_cta"
              icon={props?.secondaryCta?.variant === 'ghost' ? <ArrowNextIcon /> : undefined}
              onClick={trackClick('promo_template_secondary_cta', props.secondaryCta?.target)}
            >
              {props.secondaryCta?.label}
            </PreservedUtmButton>
          )}
        </div>
      )}
    </div>
  );

  return <ModuleLayout assembly={topImageOnPhone(props)} renderContent={contentPanel} renderImage={imagePanel} />;
}

export default InformationModule;
